<script setup lang="ts" generic="Item">
import { computed, defineAsyncComponent, defineComponent, ref } from 'vue'

import type { CarouselProps } from './types'

import Alert from '@/shared/ui/alert/Alert.vue'
import Button from '@/shared/ui/button/Button.vue'
import Skeleton from '@/shared/ui/skeleton/Skeleton.vue'

const props = withDefaults(defineProps<CarouselProps<Item>>(), {
  slidesPerView: 2,
  spaceBetween: 20,
  state: 'ready',
})
const resolvedSlidesPerGroup = computed(() => props.slidesPerGroup ?? props.slidesPerView)
// Forward caller-owned arrows only when supplied: presence (even a null ref) is the signal.
const navigationBinding = computed(() =>
  props.prevEl === undefined && props.nextEl === undefined
    ? {}
    : { prevEl: props.prevEl ?? null, nextEl: props.nextEl ?? null },
)

const loadFailed = ref(false)
const attempt = ref(0)
const EmptyFailure = defineComponent({ name: 'CarouselLoadError', render: () => null })

const CarouselInner = defineAsyncComponent({
  loader: () => import('./CarouselInner.vue'),
  errorComponent: EmptyFailure,
  onError(_error, _retry, fail) {
    loadFailed.value = true
    fail()
  },
})

function retry(): void {
  loadFailed.value = false
  attempt.value += 1
}
</script>

<template>
  <div class="ui-carousel-shell w-full" :aria-label="ariaLabel">
    <div
      v-if="state === 'loading'"
      class="ui-carousel-shell__loading grid grid-cols-[repeat(auto-fit,minmax(12rem,1fr))] gap-md"
    >
      <Skeleton v-for="index in Math.max(1, slidesPerView)" :key="index" height="12rem" />
    </div>
    <Alert v-else-if="state === 'error' || loadFailed" tone="danger">
      {{ loadErrorMessage }}
      <Button size="sm" variant="outline" :aria-label="retryLabel" @click="retry">
        {{ retryLabel }}
      </Button>
    </Alert>
    <slot v-else-if="state === 'empty' || items.length === 0" name="empty" />
    <CarouselInner
      v-else
      :key="attempt"
      v-bind="{ ariaLabel, ...navigationBinding }"
      :items="items"
      :slides-per-view="slidesPerView"
      :slides-per-group="resolvedSlidesPerGroup"
      :space-between="spaceBetween"
      :previous-label="previousLabel"
      :next-label="nextLabel"
      :load-error-message="loadErrorMessage"
      :retry-label="retryLabel"
      :state="state"
    >
      <template #default="slotProps">
        <slot v-bind="slotProps" />
      </template>
    </CarouselInner>
  </div>
</template>
